import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { createCanvasSlice } from './slices/canvas.slice';
import { createToolsSlice } from './slices/tools.slice';
import { createInputSlice } from './slices/input.slice';
import { createDrawingOpsSlice } from './slices/drawing-ops.slice';
import { createHistorySlice } from './slices/history.slice';
import { createPersistenceSlice } from './slices/persistence.slice';
import type { DrawingState } from './slices/types';

export type {
  DrawingTool,
  CanvasPoint,
  TextBlock,
  DrawingHistoryState,
  CanvasTransform,
  BrushSettings,
  CloneSettings,
  DrawingSession,
} from './drawing.types';

export const useDrawingStore = create<DrawingState>()(
  subscribeWithSelector(
    immer((...a) => ({
      ...createCanvasSlice(...a),
      ...createToolsSlice(...a),
      ...createInputSlice(...a),
      ...createDrawingOpsSlice(...a),
      ...createHistorySlice(...a),
      ...createPersistenceSlice(...a),
    }))
  )
);

// Selectors
export const selectTool = (state: DrawingState) => state.tool;
export const selectBrushSettings = (state: DrawingState) => state.brushSettings;
export const selectCanvas = (state: DrawingState) => state.canvas;
export const selectCanUndo = (state: DrawingState) => state.history.canUndo;
export const selectCanRedo = (state: DrawingState) => state.history.canRedo;
export const selectHasChanges = (state: DrawingState) =>
  state.canvas.hasChanges;
export const selectIsDrawing = (state: DrawingState) => state.drawing.isDrawing;
export const selectModifiers = (state: DrawingState) => state.modifiers;
export const selectUIState = (state: DrawingState) => state.ui;
export const selectSession = (state: DrawingState) => state.session;
export const selectZoom = (state: DrawingState) => state.canvas.transform.zoom;
export const selectPan = (state: DrawingState) => state.canvas.transform.pan;
